import { getEnvOrThrow } from "./env";

export const ApiPaths = {
	REFRESH_TOKEN: "api/v1/refresh_token",
	SIGNUP: "api/v1/signup",
	PROVIDER_SIGNUP: "api/v1/provider_signup",
	LOGIN: "api/v1/login",
	PROFILE: "api/v1/profile",
	// User Profile & Dashboard
	USER_PROFILE: "api/v1/user/profile",
	USER_PASSWORD: "api/v1/user/password",
	USER_DASHBOARD: "api/v1/user/dashboard",
	// Auth flows
	AUTH_VERIFY_EMAIL: "api/v1/auth/verify-email",
	AUTH_RESEND_VERIFICATION: "api/v1/auth/resend-verification",
	AUTH_FORGOT_PASSWORD: "api/v1/auth/forgot-password",
	AUTH_RESET_PASSWORD: "api/v1/auth/reset-password",
	PROVIDERS: "api/v1/providers",
	PRODUCTS: "api/v1/products",
	EXPERIENCES: "api/v1/experiences",
	FEATURED_PROVIDERS: "api/v1/featured_providers",
	POPULAR_OFFERINGS: "api/v1/popular_offerings",
	HOME: "api/v1/home",
	FOOTER: "api/v1/footer",
	PRODUCT: (id: number) => `api/v1/products/${id}` as const,
	PRODUCT_SIMILAR: (id: number) => `api/v1/products/${id}/similar` as const,
	EXPERIENCE: (id: number) => `api/v1/experiences/${id}` as const,
	EXPERIENCE_SIMILAR: (id: number) => `api/v1/experiences/${id}/similar` as const,
	BLOG_POSTS: "api/v1/blog_posts",
	BLOG_POST: (id: number) => `api/v1/blog_posts/${id}` as const,
	ADMIN_BLOG_POSTS: "api/v1/admin/blog_posts",
	ADMIN_BLOG_POST: (id: number) => `api/v1/admin/blog_posts/${id}` as const,
	ADMIN_BLOG_POST_FEATURED: (id: number) => `api/v1/admin/blog_posts/${id}/update_featured` as const,
	VILLAGES: "api/v1/villages",
	VILLAGE: (id: string | number) => `api/v1/villages/${id}` as const,
	// Beaches (from database)
	BEACHES: "api/v1/beaches",
	BEACH: (slug: string) => `api/v1/beaches/${slug}` as const,
	BEACH_REVIEWS: (slug: string) => `api/v1/beaches/${slug}/reviews` as const,
	BEACH_WEATHER: (slug: string) => `api/v1/beaches/${slug}/weather` as const,
	BEACHES_FILTER: (filter: string) => `api/v1/beaches/filter/${filter}` as const,
	VILLAGE_BEACHES: (villageSlug: string) => `api/v1/villages/${villageSlug}/beaches` as const,
	VILLAGE_PROVIDERS: (id: string | number) => `api/v1/villages/${id}/providers` as const,
	VILLAGE_PRODUCTS: (id: string | number) => `api/v1/villages/${id}/products` as const,
	VILLAGE_EXPERIENCES: (id: string | number) => `api/v1/villages/${id}/experiences` as const,
	FILTERS: "api/v1/filters",
	PROVIDER: (id: number) => `api/v1/providers/${id}` as const,
	NEWSLETTER_SUBSCRIBE: "api/v1/newsletter",
	NEWSLETTER_SIGNUPS: "api/v1/newsletter",
	ACCESSIBLE_PROVIDERS: "api/v1/providers/accessible",
	SEARCH: "api/v1/search",
	PUBLIC_STATS: "api/v1/stats",
	WEATHER: "api/v1/weather",
	PROVIDER_EXPERIENCES: (providerId: number) => `api/v1/providers/${providerId}/experiences` as const,
	ADMIN_STATS: "api/v1/admin/stats",
	ADMIN_PROVIDERS: "api/v1/admin/providers",
	ADMIN_PROVIDER_APPROVAL: (id: number) => `api/v1/admin/providers/${id}/update_approval` as const,
	ADMIN_PROVIDER_FEATURED: (id: number) => `api/v1/admin/providers/${id}/update_featured` as const,
	ADMIN_USERS: "api/v1/admin/users",
	ADMIN_USERS_SEARCH: "api/v1/admin/users/search",
	ADMIN_USER: (id: number) => `api/v1/admin/users/${id}` as const,
	ADMIN_USER_APPROVAL: (id: number) => `api/v1/admin/users/${id}/update_approval` as const,
	ADMIN_PRODUCTS: "api/v1/admin/products",
	ADMIN_PRODUCT_FLAGGED: (id: number) => `api/v1/admin/products/${id}/update_flagged` as const,
	ADMIN_EXPERIENCES: "api/v1/admin/experiences",
	ADMIN_EXPERIENCE: (id: number) => `api/v1/admin/experiences/${id}` as const,
	ADMIN_EXPERIENCE_FLAGGED: (id: number) => `api/v1/admin/experiences/${id}/update_flagged` as const,
	ADMIN_PROVIDER_FLAGGED: (id: number) => `api/v1/admin/providers/${id}/update_flagged` as const,
	PROVIDER_ANALYTICS: (id: number) => `api/v1/providers/${id}/analytics` as const,
	PROVIDER_BUSINESS_ENTITY: (providerId: number) => `api/v1/providers/${providerId}/business_entity` as const,
	// Events Calendar
	LOCAL_EVENTS: "api/v1/local_events",
	LOCAL_EVENT: (id: number) => `api/v1/local_events/${id}` as const,
	LOCAL_EVENTS_REGIONS: "api/v1/local_events/regions",
	EVENT_SERIES: (slug: string) => `api/v1/event_series/${slug}` as const,
	// Admin Event Sources
	ADMIN_EVENT_SOURCES: "api/v1/admin/event_sources",
	ADMIN_EVENT_SOURCE: (id: number) => `api/v1/admin/event_sources/${id}` as const,
	ADMIN_EVENT_SOURCE_SYNC: (id: number) => `api/v1/admin/event_sources/${id}/sync` as const,
	ADMIN_EVENT_SOURCE_RESET_ERROR: (id: number) => `api/v1/admin/event_sources/${id}/reset_error` as const,
	// Admin Local Events
	ADMIN_LOCAL_EVENTS: "api/v1/admin/local_events",
	ADMIN_LOCAL_EVENT: (id: number) => `api/v1/admin/local_events/${id}` as const,
	ADMIN_LOCAL_EVENT_SERIES_SUGGESTIONS: (id: number) => `api/v1/admin/local_events/${id}/series_suggestions` as const,
	// Admin Event Series (the recurring, curated ones)
	ADMIN_EVENT_SERIES: "api/v1/admin/event_series",
	ADMIN_EVENT_SERIES_ITEM: (id: number) => `api/v1/admin/event_series/${id}` as const,
	ADMIN_EVENT_SERIES_OCCURRENCE_SUGGESTIONS: (id: number) => `api/v1/admin/event_series/${id}/occurrence_suggestions` as const,
	// Labels
	LABELS: "api/v1/labels",
	ADMIN_LABELS: "api/v1/admin/labels",
	ADMIN_LABEL: (id: number) => `api/v1/admin/labels/${id}` as const,
	// Certifications
	CERTIFICATIONS: "api/v1/certifications",
	CERTIFICATION: (id: number | string) => `api/v1/certifications/${id}` as const,
	PROVIDER_CERTIFICATIONS: "api/v1/provider/certifications",
	PROVIDER_CERTIFICATION: (id: number) => `api/v1/provider/certifications/${id}` as const,
	ADMIN_CERTIFICATIONS: "api/v1/admin/certifications",
	ADMIN_CERTIFICATION: (id: number) => `api/v1/admin/certifications/${id}` as const,
	ADMIN_PROVIDER_CERTIFICATIONS: "api/v1/admin/provider_certifications",
	ADMIN_PROVIDER_CERTIFICATION_VERIFY: (id: number) => `api/v1/admin/provider_certifications/${id}/verify` as const,
	ADMIN_PROVIDER_CERTIFICATION_UNVERIFY: (id: number) => `api/v1/admin/provider_certifications/${id}/unverify` as const,
	// Admin Villages
	ADMIN_VILLAGES: "api/v1/admin/villages",
	ADMIN_VILLAGE: (id: number) => `api/v1/admin/villages/${id}` as const,
	// Filtered Results
	FILTERED_BY_CATEGORY: (categoryType: string, categorySlug: string) =>
		`api/v1/category/${categoryType}/${categorySlug}` as const,
	FILTERED_BY_TAG: (tagSlug: string) => `api/v1/tags/${tagSlug}` as const,
	// Submissions
	SUBMISSIONS: "api/v1/submissions",
	ADMIN_SUBMISSIONS: "api/v1/admin/submissions",
	ADMIN_SUBMISSION: (id: number) => `api/v1/admin/submissions/${id}` as const,
	ADMIN_SUBMISSION_MARK_READ: (id: number) => `api/v1/admin/submissions/${id}/mark_read` as const,
	ADMIN_SUBMISSION_COMMENTS: (id: number) => `api/v1/admin/submissions/${id}/comments` as const,
	ADMIN_SUBMISSION_COMMENT: (submissionId: number, commentId: number) =>
		`api/v1/admin/submissions/${submissionId}/comments/${commentId}` as const,
	ADMIN_SUBMISSIONS_COUNTS: "api/v1/admin/submissions_counts",
	// Venues
	VENUES: "api/v1/venues",
	VENUE: (ref: number | string) => `api/v1/venues/${ref}` as const,
	VENUE_SIMILAR: (ref: number | string) => `api/v1/venues/${ref}/similar` as const,
	VENUE_ITEMS: (venueId: number) => `api/v1/venues/${venueId}/venue_items` as const,
	VENUE_ITEM: (venueId: number, itemId: number) => `api/v1/venues/${venueId}/venue_items/${itemId}` as const,
	PROVIDER_VENUES: (providerId: number) => `api/v1/providers/${providerId}/venues` as const,
	// Admin Venues
	ADMIN_VENUES: "api/v1/admin/venues",
	ADMIN_VENUE: (id: number) => `api/v1/admin/venues/${id}` as const,
	ADMIN_VENUE_FLAGGED: (id: number) => `api/v1/admin/venues/${id}/update_flagged` as const,
	ADMIN_VENUE_PUBLISH_NOW: (id: number) => `api/v1/admin/venues/${id}/publish_now` as const,
	ADMIN_CREATE_VENUE: "api/v1/admin/venues/create_with_provider",
	// Provider Booking Settings
	PROVIDER_BOOKING_SETTINGS: "api/v1/provider/booking_settings",
	PROVIDER_BOOKING_SETTINGS_TOGGLE_TYPE: "api/v1/provider/booking_settings/toggle_type",
	// Provider Bookings
	PROVIDER_BOOKINGS: "api/v1/provider/bookings",
	PROVIDER_BOOKING: (id: number) => `api/v1/provider/bookings/${id}` as const,
	PROVIDER_BOOKING_CONFIRM: (id: number) => `api/v1/provider/bookings/${id}/confirm` as const,
	PROVIDER_BOOKING_CANCEL: (id: number) => `api/v1/provider/bookings/${id}/cancel` as const,
	PROVIDER_BOOKING_COMPLETE: (id: number) => `api/v1/provider/bookings/${id}/complete` as const,
	PROVIDER_BOOKING_NO_SHOW: (id: number) => `api/v1/provider/bookings/${id}/no_show` as const,
	PROVIDER_BOOKING_FORCE_CONFIRM: (id: number) => `api/v1/provider/bookings/${id}/force_confirm` as const,
	PROVIDER_BOOKING_FLAGS: (id: number) => `api/v1/provider/bookings/${id}/flags` as const,
	PROVIDER_BOOKING_MARK_READ: (id: number) => `api/v1/provider/bookings/${id}/mark_read` as const,
	PROVIDER_BOOKING_NOTES: (id: number) => `api/v1/provider/bookings/${id}/notes` as const,
	PROVIDER_BOOKINGS_STATS: "api/v1/provider/bookings/stats",
	PROVIDER_BOOKINGS_CALENDAR: "api/v1/provider/bookings/calendar",
	// Experience Booking Config
	EXPERIENCE_BOOKING_CONFIG: (experienceId: number) => `api/v1/provider/experiences/${experienceId}/booking_config` as const,
	EXPERIENCE_BOOKING_CONFIG_TOGGLE: (experienceId: number) => `api/v1/provider/experiences/${experienceId}/booking_config/toggle` as const,
	// Slot Templates
	SLOT_TEMPLATES: (experienceId: number) => `api/v1/provider/experiences/${experienceId}/slot_templates` as const,
	SLOT_TEMPLATE: (experienceId: number, templateId: number) => `api/v1/provider/experiences/${experienceId}/slot_templates/${templateId}` as const,
	// Booking Slots
	PROVIDER_BOOKING_SLOTS: "api/v1/provider/booking_slots",
	PROVIDER_BOOKING_SLOT: (id: number) => `api/v1/provider/booking_slots/${id}` as const,
	PROVIDER_BOOKING_SLOTS_GENERATE: "api/v1/provider/booking_slots/generate",
	PROVIDER_BOOKING_SLOT_BLOCK: (id: number) => `api/v1/provider/booking_slots/${id}/block` as const,
	PROVIDER_BOOKING_SLOT_UNBLOCK: (id: number) => `api/v1/provider/booking_slots/${id}/unblock` as const,
	// Waitlist
	PROVIDER_WAITLIST: "api/v1/provider/waitlist",
	PROVIDER_WAITLIST_PROMOTE: (id: number) => `api/v1/provider/waitlist/${id}/promote` as const,
	// Customer Booking Endpoints
	EXPERIENCE_AVAILABILITY: (experienceId: number) => `api/v1/experiences/${experienceId}/availability` as const,
	EXPERIENCE_PRICING: (experienceId: number) => `api/v1/experiences/${experienceId}/availability/pricing` as const,
	BOOKINGS: "api/v1/bookings",
	BOOKING_LOOKUP: "api/v1/bookings/lookup",
	BOOKING_CANCEL: (id: number) => `api/v1/bookings/${id}/cancel` as const,
	USER_BOOKINGS: "api/v1/user/bookings",
	USER_BOOKING: (id: number) => `api/v1/user/bookings/${id}` as const,
	USER_BOOKING_CANCEL: (id: number) => `api/v1/user/bookings/${id}/cancel` as const,
	// User Beach Interactions
	USER_BEACH_FAVORITES: "api/v1/user/beach_favorites",
	USER_BEACH_FAVORITE: (beachSlug: string) => `api/v1/user/beach_favorites/${beachSlug}` as const,
	USER_BEACH_PLANNED_VISITS: "api/v1/user/beach_planned_visits",
	USER_BEACH_PLANNED_VISIT: (beachSlug: string) => `api/v1/user/beach_planned_visits/${beachSlug}` as const,
	USER_BEACH_VISITS: "api/v1/user/beach_visits",
	USER_BEACH_VISIT: (id: number) => `api/v1/user/beach_visits/${id}` as const,
	// Public Provider Booking Settings (for customer booking flow)
	PUBLIC_PROVIDER_BOOKING_SETTINGS: (providerId: number) => `api/v1/providers/${providerId}/booking_settings` as const,
	// Public Experience Booking Config (for customer booking flow)
	PUBLIC_EXPERIENCE_BOOKING_CONFIG: (experienceId: number) => `api/v1/experiences/${experienceId}/booking_config` as const,
	// Customer Acceptance (public, token-based)
	BOOKING_ACCEPTANCE_STATUS: (bookingId: number) => `api/v1/bookings/${bookingId}/acceptance/status` as const,
	BOOKING_ACCEPTANCE_ACCEPT: (bookingId: number) => `api/v1/bookings/${bookingId}/acceptance/accept` as const,
	BOOKING_ACCEPTANCE_DECLINE: (bookingId: number) => `api/v1/bookings/${bookingId}/acceptance/decline` as const,
	// Provider email actions (public, token-based). The token is a path segment.
	BOOKING_ACTION: (bookingId: number, action: "confirm" | "cancel", token: string) =>
		`api/v1/booking_actions/${bookingId}/${action}/${encodeURIComponent(token)}` as const,
	// Trip Planner
	ACTIVITIES: "api/v1/activities",
	ACTIVITY: (slugOrId: string | number) => `api/v1/activities/${slugOrId}` as const,
	INTEREST_CATEGORIES: "api/v1/interest_categories",
	TRIP_PLANNER_PREFERENCES: "api/v1/trip_planner/preferences",
	TRIP_PLANNER_PREFERENCE: (id: number) => `api/v1/trip_planner/preferences/${id}` as const,
	TRIP_PLANNER_GENERATE: "api/v1/trip_planner/generate",
	// Public itinerary access by share token
	TRIP_PLANNER_ITINERARY: (shareToken: string) => `api/v1/trip_planner/itineraries/${shareToken}` as const,
	TRIP_PLANNER_ITINERARY_EXPORT: (shareToken: string) => `api/v1/trip_planner/itineraries/${shareToken}/export` as const,
	USER_ITINERARIES: "api/v1/user/itineraries",
	USER_ITINERARY: (id: number) => `api/v1/user/itineraries/${id}` as const,
	USER_ITINERARY_REGENERATE: (id: number) => `api/v1/user/itineraries/${id}/regenerate` as const,
	USER_ITINERARY_DUPLICATE: (id: number) => `api/v1/user/itineraries/${id}/duplicate` as const,
	USER_ITINERARY_EXPORT: (id: number) => `api/v1/user/itineraries/${id}/export` as const,
	// Action Logs Analytics (Admin)
	ADMIN_ACTION_LOGS: "api/v1/admin/action_logs",
	ADMIN_ACTION_LOGS_DAILY_BREAKDOWN: "api/v1/admin/action_logs/daily_breakdown",
	ADMIN_ACTION_LOGS_SEARCHES: "api/v1/admin/action_logs/searches",
	ADMIN_ACTION_LOGS_CONTACTS: "api/v1/admin/action_logs/contacts",
	// Action Tracking (Public)
	TRACK_PROVIDER_CONTACT: "api/v1/track/provider_contact",
	TRACK_EXPERIENCE_CONTACT: "api/v1/track/experience_contact",
	TRACK_EVENT_LINK_CLICK: "api/v1/track/event_link_click",
	TRACK_GUIDE_OFFICIAL_LINK_CLICK: "api/v1/track/guide_official_link_click",
	TODAY_BAND: "api/v1/today",
	// The island map: every point it draws, one request. Never takes a position.
	MAP_POINTS: "api/v1/map/points",
	GUIDE_ARTICLES: "api/v1/guide_articles",
	GUIDE_ARTICLE: (slug: string) => `api/v1/guide_articles/${slug}` as const,
	GUIDE_PAGE_LINKS: "api/v1/guide_articles/page_links",
	TRACK_SEARCH: "api/v1/track/search",
	// Local Flavors (Public)
	LOCAL_FLAVORS: "api/v1/local_flavors",
	LOCAL_FLAVOR: (slug: string) => `api/v1/local_flavors/${slug}` as const,
	// Krkpedia (Public, read-only encyclopedia)
	KRKPEDIA: "api/v1/krkpedia",
	KRKPEDIA_ENTRY: (slug: string) => `api/v1/krkpedia/${slug}` as const,
	KRKPEDIA_RELATED: (slug: string) => `api/v1/krkpedia/${slug}/related` as const,
	// Locals near whatever the entry is about. Krkpedia is 69% of the sitemap
	// and linked almost nowhere commercial before this.
	KRKPEDIA_NEARBY: (slug: string) => `api/v1/krkpedia/${slug}/nearby` as const,

	// Every landing cell with indexability already decided server-side. One
	// request: the list is small and the prerender needs all of it anyway.
	LANDING_PAGES: "api/v1/landing_pages" as const,
	KRKPEDIA_FEATURED: "api/v1/krkpedia/featured",
	KRKPEDIA_SEARCH: "api/v1/krkpedia/search",
	KRKPEDIA_TAGS: "api/v1/krkpedia/tags",
	KRKPEDIA_TAG: (tag: string) => `api/v1/krkpedia/tags/${tag}` as const,
	KRKPEDIA_CATEGORIES: "api/v1/krkpedia/categories",
	// User Village Visits
	USER_VILLAGE_VISITS: "api/v1/user/village_visits",
	USER_VILLAGE_VISIT: (villageId: number) => `api/v1/user/village_visits/${villageId}` as const,
	// User Flavor Tries
	USER_FLAVOR_TRIES: "api/v1/user/flavor_tries",
	USER_FLAVOR_TRY: (flavorSlug: string) => `api/v1/user/flavor_tries/${flavorSlug}` as const,
	// Nomad Spaces (Public)
	NOMAD_SPACES: "api/v1/nomad_spaces",
	NOMAD_SPACE: (slug: string) => `api/v1/nomad_spaces/${slug}` as const,
	NOMAD_SPACES_CATEGORIES: "api/v1/nomad_spaces/categories",
	// Recipes (Public)
	RECIPES: "api/v1/recipes",
	RECIPE: (slug: string) => `api/v1/recipes/${slug}` as const,
	RECIPES_FEATURED: "api/v1/recipes/featured",
	// User Recipe Favorites
	USER_RECIPE_FAVORITES: "api/v1/user/recipe_favorites",
	USER_RECIPE_FAVORITE: (slug: string) => `api/v1/user/recipe_favorites/${slug}` as const,
	// Admin Recipes
	ADMIN_RECIPES: "api/v1/admin/recipes",
	ADMIN_RECIPE: (id: number) => `api/v1/admin/recipes/${id}` as const,
	ADMIN_RECIPE_FEATURED: (id: number) => `api/v1/admin/recipes/${id}/update_featured` as const,
	// Footer Featured (24h cached)
	FOOTER_PROVIDERS: "api/v1/footer/providers",
	FOOTER_PRODUCTS: "api/v1/footer/products",
	FOOTER_EXPERIENCES: "api/v1/footer/experiences",
	FOOTER_VILLAGES: "api/v1/footer/villages",
	// Session
	LOGOUT: "api/v1/logout",
	// Reviews (public — nested under each reviewable)
	PROVIDER_REVIEWS: (providerId: number) => `api/v1/providers/${providerId}/reviews` as const,
	PRODUCT_REVIEWS: (productId: number) => `api/v1/products/${productId}/reviews` as const,
	EXPERIENCE_REVIEWS: (experienceId: number) => `api/v1/experiences/${experienceId}/reviews` as const,
	// Guest review invitation (public, token-based — no auth)
	REVIEW_INVITATION: (token: string) => `api/v1/reviews/invitation/${token}` as const,
	// Admin Reviews
	ADMIN_REVIEWS: "api/v1/admin/reviews",
	ADMIN_REVIEW: (id: number) => `api/v1/admin/reviews/${id}` as const,
	ADMIN_REVIEW_APPROVE: (id: number) => `api/v1/admin/reviews/${id}/approve` as const,
	ADMIN_REVIEW_REJECT: (id: number) => `api/v1/admin/reviews/${id}/reject` as const,
	ADMIN_REVIEW_FLAGGED: (id: number) => `api/v1/admin/reviews/${id}/update_flagged` as const,
	ADMIN_REVIEWS_COUNTS: "api/v1/admin/reviews_counts",
	// Admin Bookings
	ADMIN_BOOKINGS: "api/v1/admin/bookings",
	ADMIN_BOOKING: (id: number) => `api/v1/admin/bookings/${id}` as const,
	ADMIN_BOOKING_CANCEL: (id: number) => `api/v1/admin/bookings/${id}/cancel` as const,
	ADMIN_BOOKINGS_STATS: "api/v1/admin/bookings/stats",
	// Admin Booking Reports
	ADMIN_BOOKING_REPORTS_SUMMARY: "api/v1/admin/booking_reports/summary",
	ADMIN_BOOKING_REPORTS_BY_PROVIDER: "api/v1/admin/booking_reports/by_provider",
	ADMIN_BOOKING_REPORTS_BY_EXPERIENCE: "api/v1/admin/booking_reports/by_experience",
	ADMIN_BOOKING_REPORTS_BY_DATE: "api/v1/admin/booking_reports/by_date",
	// Admin Booking Settings (platform-wide)
	ADMIN_BOOKING_SETTINGS: "api/v1/admin/booking_settings",
	ADMIN_BOOKING_SETTINGS_ADD_FLAG: "api/v1/admin/booking_settings/add_flag",
	ADMIN_BOOKING_SETTINGS_REMOVE_FLAG: "api/v1/admin/booking_settings/remove_flag",
	// Inquiry mailto: templates — public read, owner-only edit
	INQUIRY_SETTINGS: "api/v1/inquiry_settings",
	ADMIN_INQUIRY_SETTINGS: "api/v1/admin/inquiry_settings",
	// Admin per-provider booking control
	ADMIN_PROVIDER_BOOKING_CONFIG: (id: number) => `api/v1/admin/providers/${id}/booking_config` as const,
	ADMIN_PROVIDER_ENABLE_BOOKING: (id: number) => `api/v1/admin/providers/${id}/enable_booking` as const,
	ADMIN_PROVIDER_DISABLE_BOOKING: (id: number) => `api/v1/admin/providers/${id}/disable_booking` as const,
	// Admin provider certification delete
	ADMIN_PROVIDER_CERTIFICATION: (id: number) => `api/v1/admin/provider_certifications/${id}` as const,
	// Direct-to-storage upload presign
	DIRECT_UPLOADS: "api/v1/direct_uploads" as const,
	// Admin background jobs (Solid Queue state + job_runs history)
	ADMIN_BACKGROUND_JOBS_OVERVIEW: "api/v1/admin/background_jobs/overview",
	ADMIN_BACKGROUND_JOBS_QUEUES: "api/v1/admin/background_jobs/queues",
	ADMIN_BACKGROUND_JOBS_PROCESSES: "api/v1/admin/background_jobs/processes",
	ADMIN_BACKGROUND_JOBS_RECURRING: "api/v1/admin/background_jobs/recurring",
	ADMIN_BACKGROUND_JOB_RUNS: "api/v1/admin/background_jobs/runs",
	ADMIN_BACKGROUND_JOB_RUN: (id: number) => `api/v1/admin/background_jobs/runs/${id}` as const,
	ADMIN_BACKGROUND_JOB_FAILURES: "api/v1/admin/background_jobs/failures",
	ADMIN_BACKGROUND_JOB_FAILURE: (id: number) =>
		`api/v1/admin/background_jobs/failures/${id}` as const,
	ADMIN_BACKGROUND_JOB_FAILURE_RETRY: (id: number) =>
		`api/v1/admin/background_jobs/failures/${id}/retry` as const,
	ADMIN_BACKGROUND_JOB_FAILURE_DISCARD: (id: number) =>
		`api/v1/admin/background_jobs/failures/${id}/discard` as const,
	ADMIN_BACKGROUND_JOB_FAILURES_RETRY_ALL: "api/v1/admin/background_jobs/failures/retry_all",
	ADMIN_BACKGROUND_JOB_RUN_NOW: "api/v1/admin/background_jobs/run_now",
	ADMIN_BACKGROUND_JOB_QUEUE_PAUSE: (name: string) =>
		`api/v1/admin/background_jobs/queues/${encodeURIComponent(name)}/pause` as const,
	ADMIN_BACKGROUND_JOB_QUEUE_RESUME: (name: string) =>
		`api/v1/admin/background_jobs/queues/${encodeURIComponent(name)}/resume` as const,
} as const;

type StaticEndpoints = {
  [K in keyof typeof ApiPaths]: typeof ApiPaths[K] extends (...args: never[]) => unknown
    ? ReturnType<typeof ApiPaths[K]>
    : typeof ApiPaths[K];
};
export type Endpoint = StaticEndpoints[keyof StaticEndpoints];

/**
 * Values that can go in a query string.
 *
 * `undefined` and `null` are allowed here and DROPPED by getFullUrl, because
 * callers naturally build params from optional state — `{ sort, direction }`
 * where either may be unset. Passing such an object straight to
 * `new URLSearchParams()` serialises the literal strings "undefined" and
 * "null" into the URL, which is what used to happen.
 */
export type UrlParams = Record<
	string,
	string | number | boolean | null | undefined
>;

export function getFullUrl(path: Endpoint, urlParams: UrlParams = {}) {
	const params = new URLSearchParams();
	for (const [key, value] of Object.entries(urlParams)) {
		if (value === undefined || value === null) continue;
		params.set(key, String(value));
	}
	const host = getEnvOrThrow("VITE_API_HOST");
	const queryString = params.toString();

	return queryString ? `${host}/${path}?${queryString}` : `${host}/${path}`;
}
